import { apiFetch } from "@/lib/api-client";
import { FALLBACK_RATE_CARD } from "@/lib/whatsappCost/fallbackRateCard";
import type { RateCard, RateVersion } from "@/lib/whatsappCost/types";
import {
  getMockAdminTenants,
  getMockBillingStatus,
  getMockBroadcastEstimate,
  getMockRateCard,
  getMockUsage,
} from "./whatsappBillingMock";
import type {
  AdminBillingSettings,
  AdminTenantsResponse,
  AdminTenantUsage,
  BroadcastEstimateApiResponse,
  CloseMonthResult,
  MonthStatementsResponse,
  RateCardApiResponse,
  TenantBillingPatch,
  TenantBillingResult,
  TenantStatus,
  TenantWallet,
  TenantWalletPatch,
  WhatsappBillingStatusResponse,
  WhatsappUsageResponse,
} from "./whatsappBillingTypes";

/**
 * Mock data is a CHOICE (`VITE_BILLING_MOCK=1`), never a fallback. A failed or missing endpoint
 * used to be answered with invented usage, an invented billing status and invented tenants,
 * which put fabricated cost numbers in front of real tenants. Every fetcher below now throws
 * instead, and the widgets already hide themselves when their query has no data.
 * The rate card is the one exception: its fallback is a real, dated card flagged `isFallback`.
 */
const IS_MOCK_FORCED =
  typeof import.meta !== "undefined" && import.meta.env?.VITE_BILLING_MOCK === "1";

/**
 * The server's snake_case card as the domain `RateCard` the pure pricing helpers read. The card
 * used to be passed through unmapped, so `effectiveFrom` was undefined and the first helper that
 * picked a rate version threw -- which blanked the broadcast wizard's review step.
 */
export function toRateCard(api: RateCardApiResponse): RateCard {
  return {
    currency: api.currency,
    gstRate: api.gst_rate,
    market: api.market,
    timezone: api.timezone || FALLBACK_RATE_CARD.timezone,
    versions: api.versions.map((version) => ({
      effectiveFrom: version.effective_from,
      rates: version.rates as RateVersion["rates"],
      serviceFreeTierPerNumber: version.service_free_tier,
      utilityFreeInsideCsw: version.utility_free_in_window,
    })),
  };
}

/**
 * Fetches current RateCard from backend.
 * Degrades safely to FALLBACK_RATE_CARD (flagged `isFallback`) if the endpoint fails.
 */
export async function fetchRateCard(accountId?: string): Promise<RateCard> {
  if (IS_MOCK_FORCED) {
    return getMockRateCard();
  }
  try {
    const data = await apiFetch<RateCardApiResponse>("/api/billing/whatsapp/rate-card", { accountId });
    if (!data || !Array.isArray(data.versions) || data.versions.length === 0) {
      return FALLBACK_RATE_CARD;
    }
    return toRateCard(data);
  } catch {
    return FALLBACK_RATE_CARD;
  }
}

/**
 * One calendar month of delivered messages, priced for this tenant. `month` is "YYYY-MM";
 * omitted, the server answers the tenant's current month in the tenant's own timezone.
 * `month` is the only parameter the endpoint reads.
 */
export async function fetchWhatsappUsage(month?: string, accountId?: string): Promise<WhatsappUsageResponse> {
  if (IS_MOCK_FORCED) {
    return getMockUsage(month);
  }
  const query = month ? `?month=${encodeURIComponent(month)}` : "";
  const data = await apiFetch<WhatsappUsageResponse>(`/api/billing/whatsapp/usage${query}`, { accountId });
  if (!data || !data.totals || !Array.isArray(data.rows)) {
    throw new Error("WhatsApp usage response was malformed.");
  }
  return data;
}

/**
 * Fetches the tenant's WhatsApp billing configuration: which timezone its rate changes and
 * counters roll over in, and how long the ad-lead free window lasts.
 */
export async function fetchWhatsappBillingStatus(accountId?: string): Promise<WhatsappBillingStatusResponse> {
  if (IS_MOCK_FORCED) {
    return getMockBillingStatus();
  }
  const data = await apiFetch<WhatsappBillingStatusResponse>("/api/billing/whatsapp/status", { accountId });
  if (!data || !data.timezone) {
    throw new Error("WhatsApp billing status response was malformed.");
  }
  return data;
}

/**
 * Fetches broadcast audience breakdown for live estimate calculation.
 */
export async function fetchBroadcastEstimate(body: {
  templateId?: number | string | undefined;
  category?: string | undefined;
  segmentId?: number | null | undefined;
  totalRecipients?: number | undefined;
}): Promise<BroadcastEstimateApiResponse> {
  if (IS_MOCK_FORCED) {
    return getMockBroadcastEstimate(body.totalRecipients ?? 1000);
  }
  return apiFetch<BroadcastEstimateApiResponse>("/api/broadcast/estimate", {
    method: "POST",
    body,
  });
}

/**
 * Fetches Platform Admin tenant billing metrics.
 */
export async function fetchAdminTenants(): Promise<AdminTenantsResponse> {
  if (IS_MOCK_FORCED) {
    return getMockAdminTenants();
  }
  return apiFetch<AdminTenantsResponse>("/api/admin/tenants");
}

/** The platform default margin, and where it comes from. */
export function fetchBillingSettings(): Promise<AdminBillingSettings> {
  return apiFetch<AdminBillingSettings>("/api/admin/billing/settings");
}

/** Sets the platform default margin; null goes back to the deploy's configured value. */
export function saveDefaultMargin(defaultMarginPercent: number | null): Promise<AdminBillingSettings> {
  return apiFetch<AdminBillingSettings>("/api/admin/billing/settings", {
    method: "PUT",
    body: { default_margin_percent: defaultMarginPercent },
  });
}

/** One tenant's margin override and/or billing channel. Only the fields in `patch` change. */
export function patchTenantBilling(workspaceId: string, patch: TenantBillingPatch): Promise<TenantBillingResult> {
  return apiFetch<TenantBillingResult>(`/api/admin/tenants/${encodeURIComponent(workspaceId)}/billing`, {
    method: "PATCH",
    body: patch,
  });
}

function tenantPath(workspaceId: string, rest: string): string {
  return `/api/admin/tenants/${encodeURIComponent(workspaceId)}/${rest}`;
}

/** One tenant's wallet: postpaid or prepaid, the balance, and whether it has paused them. */
export function fetchTenantWallet(workspaceId: string): Promise<TenantWallet> {
  return apiFetch<TenantWallet>(tenantPath(workspaceId, "wallet"));
}

/** Switch postpaid/prepaid, or set the balance a prepaid tenant pauses at. */
export function patchTenantWallet(workspaceId: string, patch: TenantWalletPatch): Promise<TenantWallet> {
  return apiFetch<TenantWallet>(tenantPath(workspaceId, "wallet"), { method: "PATCH", body: patch });
}

/** Add money to a tenant's wallet, recorded with the admin who added it. */
export function addTenantWalletCredit(
  workspaceId: string,
  credit: { amount: number; note?: string | undefined },
): Promise<TenantWallet> {
  return apiFetch<TenantWallet>(tenantPath(workspaceId, "wallet/credits"), { method: "POST", body: credit });
}

/** How many seats a tenant may hold; null puts it back on the platform's ceiling. */
export function setTenantSeats(workspaceId: string, seatLimit: number | null): Promise<unknown> {
  return apiFetch<unknown>(tenantPath(workspaceId, "seats"), { method: "PATCH", body: { seat_limit: seatLimit } });
}

/** The platform admin's lifecycle switch for one tenant. */
export function setTenantStatus(workspaceId: string, status: TenantStatus, reason?: string): Promise<unknown> {
  return apiFetch<unknown>(tenantPath(workspaceId, "status"), {
    method: "PATCH",
    body: reason ? { status, reason } : { status },
  });
}

/** One tenant's month: traffic, our pricing, Meta's own figures, and the gap between them. */
export function fetchTenantUsage(workspaceId: string, month: string): Promise<AdminTenantUsage> {
  return apiFetch<AdminTenantUsage>(
    `/api/admin/tenants/${encodeURIComponent(workspaceId)}/usage?month=${encodeURIComponent(month)}`,
  );
}

/** Every tenant's statement for one "YYYY-MM" month. */
export function fetchMonthStatements(month: string): Promise<MonthStatementsResponse> {
  return apiFetch<MonthStatementsResponse>(`/api/admin/billing/statements?month=${encodeURIComponent(month)}`);
}

/** Locks every tenant's statement for a finished month. Idempotent; 409 while it is too early. */
export function closeMonth(month: string): Promise<CloseMonthResult> {
  return apiFetch<CloseMonthResult>(`/api/admin/billing/statements/${encodeURIComponent(month)}/close`, {
    method: "POST",
  });
}
